import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getProducts } from '../services/api';
import { Search, X, Loader2, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ProductMultiSelect = ({ selectedProductNames = [], onChange }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [products, setProducts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const dropdownRef = useRef(null);
    const observer = useRef();

    // Fetch products helper
    const fetchProducts = useCallback(async (search, pageNum, isNewSearch = false) => {
        try {
            setLoading(true);
            const res = await getProducts({ search, page: pageNum, limit: 15 });
            const newProducts = res?.data || res || [];

            if (isNewSearch) {
                setProducts(newProducts);
            } else {
                setProducts(prev => [...prev, ...newProducts]);
            }

            setHasMore(newProducts.length === 15);
        } catch (err) {
            console.error("Error fetching products:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial load and search
    useEffect(() => {
        const timer = setTimeout(() => {
            setPage(1);
            fetchProducts(searchTerm, 1, true);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm, fetchProducts]);

    // Handle Infinite Scroll
    const lastProductElementRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => {
                    const next = prevPage + 1;
                    fetchProducts(searchTerm, next);
                    return next;
                });
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore, searchTerm, fetchProducts]);

    // Normalize selected names (in case they are objects from legacy population)
    const normalizedSelectedNames = selectedProductNames.map(p =>
        typeof p === 'object' ? (p.name || "") : String(p)
    ).map(n => n.trim().toLowerCase()).filter(Boolean);

    const toggleProduct = (product) => {
        const prodName = product.name.trim().toLowerCase();
        const isSelected = normalizedSelectedNames.includes(prodName);
        let nextNames;
        if (isSelected) {
            nextNames = normalizedSelectedNames.filter(name => name !== prodName);
        } else {
            nextNames = [...normalizedSelectedNames, prodName];
        }
        onChange(nextNames);
    };

    const removeProduct = (nameToRemove) => {
        const normalizedToRemove = nameToRemove.trim().toLowerCase();
        const nextNames = normalizedSelectedNames.filter(name => name !== normalizedToRemove);
        onChange(nextNames);
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-bold text-gray-700 mb-2">
                {t('users.form.allowed_products')}
            </label>

            <div
                className="min-h-[48px] p-2 border border-gray-200 rounded-xl shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 cursor-text flex flex-wrap gap-2 items-center bg-gray-50/30"
                onClick={() => setIsOpen(true)}
            >
                {normalizedSelectedNames.map((name) => (
                    <span
                        key={name}
                        className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-2 hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-100"
                    >
                        {name}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                removeProduct(name);
                            }}
                            className="hover:text-indigo-200"
                        >
                            <X size={14} />
                        </button>
                    </span>
                ))}

                <div className="flex-1 min-w-[120px]">
                    <div className="relative">
                        <input
                            type="text"
                            autoComplete="off"
                            className="w-full border-none focus:ring-0 p-1 text-sm bg-transparent placeholder-gray-400 font-medium"
                            placeholder={normalizedSelectedNames.length === 0 ? t('users.form.placeholders.search_products') : ""}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => setIsOpen(true)}
                        />
                    </div>
                </div>
            </div>

            {isOpen && (
                <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-2xl max-h-[300px] overflow-hidden flex flex-col animate-in fade-in zoom-in duration-200">
                    <div className="p-3 border-b border-gray-50 bg-gray-50/50 flex items-center gap-2">
                        <Search size={16} className="text-gray-400" />
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t('users.form.select_products')}</span>
                    </div>

                    <div className="overflow-y-auto flex-1 custom-scrollbar">
                        {products.length === 0 && !loading && (
                            <div className="p-8 text-center text-gray-400 text-sm font-medium italic">
                                {t('users.form.messages.no_products')}
                            </div>
                        )}

                        {products.map((product, index) => {
                            const prodName = product.name.trim().toLowerCase();
                            const isSelected = normalizedSelectedNames.includes(prodName);
                            return (
                                <div
                                    key={product._id}
                                    ref={index === products.length - 1 ? lastProductElementRef : null}
                                    className={`px-4 py-3 cursor-pointer flex items-center justify-between transition-colors ${isSelected ? 'bg-indigo-50/50 text-indigo-700 font-bold' : 'hover:bg-gray-50 text-gray-700 font-medium'
                                        }`}
                                    onClick={() => toggleProduct(product)}
                                >
                                    <div>
                                        <div className="text-sm">{product.name}</div>
                                        <div className="text-[10px] text-gray-400 font-mono mt-0.5">{product.slug}</div>
                                    </div>
                                    {isSelected && <Check size={16} className="text-indigo-600 stroke-[3]" />}
                                </div>
                            );
                        })}

                        {loading && (
                            <div className="p-6 text-center">
                                <Loader2 size={24} className="animate-spin text-indigo-500 mx-auto" />
                            </div>
                        )}
                    </div>
                </div>
            )}

            <p className="mt-2 text-[10px] text-gray-400 italic font-medium px-1">
                {t('users.form.products_assigned', { count: normalizedSelectedNames.length })}
            </p>
        </div>
    );
};

export default ProductMultiSelect;
